import { NO_RISK } from "./strategyContext.js";
export function buildPhysicalMappings(input) {
    const physicalMappings = [];
    const deviceMappings = [];
    for (const controller of input.controllers) {
        for (const slot of controller.zoneSlots) {
            const zone = input.zoneModel.logicalZones.find((candidate) => candidate.id === slot.logicalZoneId);
            if (!zone) {
                continue;
            }
            const isEufh = zone.zoneKind === "eufh";
            const mappingKind = isEufh ? "central_io" : "physical_termination";
            const physicalTerminationRef = isEufh
                ? `central-load-relay-${zone.id}`
                : controller.id;
            const ruleTraceRefs = isEufh
                ? ["trace-eufh-load-points", "trace-controller-packing-start"]
                : controller.ruleTraceRefs;
            physicalMappings.push({
                id: `physical-map-${controller.id}-${zone.id}`,
                logicalOwnerRef: controller.id,
                physicalTerminationRef,
                zoneId: zone.id,
                mappingKind,
                cableImplication: isEufh
                    ? "EUFH control remains on the 710R secondary slot while the load output is recorded as a central relay termination."
                    : "Local control load terminates at the assigned controller.",
                commissioningNote: isEufh
                    ? "Commission EUFH as a secondary control zone and verify the central load-relay output mapping."
                    : "Commission directly against the assigned controller zone slot.",
                why: isEufh
                    ? "EUFH strategy ownership and physical output termination must be visible separately."
                    : "Logical owner and physical termination are the same controller for this slot.",
                how: "Built after controller allocation so every logical slot has an explicit physical mapping.",
                sourceRefs: input.sourceRefs,
                ruleTraceRefs,
                risk: NO_RISK
            });
            if (isEufh) {
                const [loadRef] = zone.loadRefs;
                deviceMappings.push({
                    id: `device-map-${loadRef ?? zone.id}-central-io`,
                    deviceRef: loadRef ?? zone.id,
                    logicalControllerRef: controller.id,
                    physicalTerminationRef,
                    zoneId: zone.id,
                    mappingType: "central_io",
                    cableImplication: "EUFH load output is a central relay termination while the zone strategy remains owned by the assigned controller.",
                    commissioningNote: "Commission the EUFH controller strategy and verify the central load relay output for this zone.",
                    why: "EUFH load mapping must expose both controller ownership and central output termination.",
                    how: "Derived from the physical central_io mapping created for the EUFH controller slot.",
                    sourceRefs: input.sourceRefs,
                    ruleTraceRefs,
                    risk: NO_RISK
                });
            }
        }
    }
    for (const towelRailDemand of input.zoneModel.pointDemands.filter((demand) => demand.demandKind.includes("towel_rail"))) {
        physicalMappings.push(makeTowelRailMapping(towelRailDemand, input.sourceRefs));
        deviceMappings.push(makeTowelRailDeviceMapping(towelRailDemand, input.sourceRefs));
    }
    return {
        physicalMappings,
        deviceMappings,
        ruleTraceRefs: [
            ...new Set([
                ...physicalMappings.flatMap((mapping) => mapping.ruleTraceRefs),
                ...deviceMappings.flatMap((mapping) => mapping.ruleTraceRefs)
            ])
        ]
    };
}
function makeTowelRailMapping(demand, sourceRefs) {
    return {
        id: `physical-map-${demand.id}`,
        logicalOwnerRef: demand.id,
        physicalTerminationRef: `central-load-relay-${demand.ownerRef}`,
        mappingKind: "central_io",
        cableImplication: "Towel rail load relay is central; it is not a controller zone and does not occupy a 710R/MZ zone slot.",
        commissioningNote: "Confirm final load-relay product and central wiring route before export.",
        why: "Towel rails are central load-relay outputs rather than control zones.",
        how: "Mapped from the towel rail point demand derived by the rules package.",
        sourceRefs,
        ruleTraceRefs: demand.ruleTraceRefs,
        risk: {
            level: "warning",
            riskRefs: ["risk-load-relay-product-mapping"],
            notes: "Load-relay quantity is deterministic; final product mapping remains open."
        }
    };
}
function makeTowelRailDeviceMapping(demand, sourceRefs) {
    return {
        id: `device-map-${demand.id}-central-io`,
        deviceRef: demand.id,
        physicalTerminationRef: `central-load-relay-${demand.ownerRef}`,
        mappingType: "central_io",
        cableImplication: "Towel rail load relay is central and is not a controller zone or local UI point.",
        commissioningNote: "Verify the towel-rail load relay output and confirm final relay product mapping before export.",
        why: "Towel rails are physical load outputs that need commissioning even though they are not control zones.",
        how: "Mapped from the towel rail point demand derived by the rules package.",
        sourceRefs,
        ruleTraceRefs: demand.ruleTraceRefs,
        risk: {
            level: "warning",
            riskRefs: ["risk-load-relay-product-mapping"],
            notes: "Load-relay quantity is deterministic; final product mapping remains open."
        }
    };
}
//# sourceMappingURL=physicalMapping.js.map